import { CheckIcon } from 'lucide-react'
import { memo, type ReactNode } from 'react'

import { Button } from '@/components/ui/button'
import {
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'

import type { FilterOption } from './data-table.interface'

interface DataTableFilterContentProps {
  label: string
  optionList: FilterOption[]
  selectedValueList: string[]
  onToggle: (value: string) => void
  onClear: () => void
}

/**
 * Renders the dropdown content with filter options and clear button.
 */
const DataTableFilterContentBase: React.FC<DataTableFilterContentProps> = ({
  label,
  optionList,
  selectedValueList,
  onToggle,
  onClear,
}): ReactNode => (
  <DropdownMenuContent align="start" className="w-[200px]">
    <DropdownMenuLabel>{label}</DropdownMenuLabel>
    <DropdownMenuSeparator />
    {optionList.map((option) => {
      const isSelected = selectedValueList.includes(option.value)
      return (
        <DropdownMenuCheckboxItem
          key={option.value}
          checked={isSelected}
          onCheckedChange={() => onToggle(option.value)}
        >
          <span className="flex-1">{option.label}</span>
          {isSelected && <CheckIcon className="size-4" />}
        </DropdownMenuCheckboxItem>
      )
    })}
    {selectedValueList.length > 0 && (
      <>
        <DropdownMenuSeparator />
        <Button variant="ghost" size="sm" className="w-full justify-center" onClick={onClear}>
          Clear filters
        </Button>
      </>
    )}
  </DropdownMenuContent>
)

export const DataTableFilterContent = memo(DataTableFilterContentBase)

DataTableFilterContent.displayName = 'DataTableFilterContent'
